import React from 'react';
import Auth from '@aws-amplify/auth';
import AppLoader from 'core/ui/components/AppLoader';
import * as RestApi from 'core/network/RestApi';
import { Types as ApiGatewayTypes } from 'services/genericAPIGatewayService/ducks';
import { Types as LdTypes } from '../../services/launchDarkly';
import { Service } from 'sections/client-management';
import { Group, Brand, Site } from 'sections/client-management/entities';
import { AppState, UserInfo, Clients, UserDetails } from './AppTypes';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { combineWithLocalFeatureFlags } from '../localFeatureFlags';
import { _hasPermission } from './impl/hasPermission';
import { _hasPermissionsAll } from './impl/hasPermissionsAll';
import { _hasPermissionsAtLeastOne } from './impl/hasPermissionsAtLeastOne';
import { _flagEnabled } from './impl/flagEnabled';
import { _allFlagsEnabled } from './impl/allFlagsEnabled';
import { _anyFlagEnabled } from './impl/anyFlagEnabled';
import { _getFlagVariation } from './impl/getFlagVariation';
import { _hasFlag } from './impl/hasFlag';
import { _hasFlags } from './impl/hasFlags';
import { _isSiteAuthorised } from './impl/isSiteAuthorised';
import { _isBrandAuthorised } from './impl/isBrandAuthorised';

const AppContext = React.createContext(null);

export function AppProvider(props) {
  const ldClient = useLDClient();
  const [signedIn, setSignedIn] = React.useState(false);
  const [userInfo, setUserInfo] = React.useState(null);
  const [userInfoError, setUserInfoError] = React.useState(null);
  const [clients, setClients] = React.useState<Clients>(
    props.clients ?? {
      sites: [],
      brands: [],
      groups: [],
    },
  );
  const [userDetails, setUserDetails] = React.useState<UserDetails>(null);
  //Loading state must be false initially in case user is not logged in
  const [isAppStateLoading, setIsAppStateLoading] = React.useState(false);

  /**
   * @deprecated Flags are only stored here for backwards compatibility to
   * support hasFlags() and will be removed in a future version in favour
   * of our new useFeatureFlag hook that relies on the LD React SDK for flag
   * storage/caching, etc.
   */
  const [flags, setFlags] = React.useState(props.flags ?? []);

  /*
    There is a lot of compromised logic in here around needing to maintain
    backwards compatibility with our legacy authentication mechanism (mainly
    around the signedIn flag). As with most of this stuff, once the rest of the
    codebase is free from legacy code we can start to look to improve the
    authentication issues as well to simplify a lot of this.
  */

  React.useEffect(() => {
    async function fetchData() {
      // default to no user - will override if someone is logged in
      let userKey = 'NoUser',
        userName = 'NoUser',
        userEmail = 'NoUser';

      if (signedIn) {
        /*
          router.tsx uses this flag to ensure all routes are inaccessible until
          all global state is loaded. This component displays a spinner on
          a while background (see below) while this flag is true.
        */
        setIsAppStateLoading(true);

        try {
          const res = await RestApi.get<UserInfo>('/users/me');
          setUserInfo(res.data);
          const {
            attributes: { sub, name, email },
          } = await Auth.currentUserInfo();

          // assign variables for use when calling LD
          userKey = sub;
          userName = name;
          userEmail = email;

          setUserDetails({
            username: name,
            email: email,
          });
          setClients(await fetchClients(props.store));

          RestApi.put(`/users/me/lastseen`);
        } catch (error) {
          /*
            FIXME: We should revisit how we handle errors here as it's not
            just user info that can fail. Maybe store an app-wide error flag
            and if it's true show an error page with a helpful error message.
          */
          if (error === 'No current user') {
            setSignedIn(false);
          } else {
            setUserInfoError(error);
          }
        }
      }

      const ldFlagsData = await ldClient.identify({ key: userKey, name: userName, email: userEmail });

      /**
       * NOTE: All the flag code below up until setFlags is for backwards
       * compatibility with legacy code. All new code uses the
       * useFeatureFlag hook which uses the LD React SDK for flag access
       * under the hood. The following code remains here for now to support
       * backwards compatibility with Redux as well as the custom useFlags
       * hook exposed by AppContext.
       */

      const combinedFlagsData = await combineWithLocalFeatureFlags(ldFlagsData);
      const flags = [];
      if (combinedFlagsData) {
        for (const [key, value] of Object.entries(combinedFlagsData)) {
          if (value) {
            flags.push(key);
            /*
                We're temporarily continuing to store flags in Redux for now
                but this is only needed for backwards compatibility with
                legacy code where flags are still accessed from Redux (mainly
                the old FeatureFlag component).
              */
            props.store.dispatch({
              type: LdTypes.KEY_COMPLETED,
              payload: {
                key,
                allowed: value,
              },
            });
          }
        }
        setFlags(flags);
      }

      /* END FLAG BACKWARD COMPATIBILITY */

      //This dispatch is for backwards compatibility with Redux code that we
      //still depend on for now.
      props.store.dispatch({ type: LdTypes.INITIALISATION_COMPLETED });

      /*
       * At this stage all global app state is loaded including:
       * - user-access-info (when signed in)
       * - feature flags (from LaunchDarkly)
       * - all groups, brands and sites
       */
      if (signedIn) {
        setIsAppStateLoading(false);
      }
    }
    fetchData();
  }, [ldClient, props.store, signedIn]);

  const updateSiteInContext = (updatedSite: Site) => {
    setClients(prev => ({
      ...prev,
      sites: prev.sites.map(site => (site.id === updatedSite.id ? { ...site, ...updatedSite } : site)),
    }));
  };

  const appState: AppState & { updateSiteInContext: (site: Site) => void } = {
    userInfo,
    signedIn,
    setSignedIn,
    userInfoError,
    flags,
    clients,
    userDetails,
    isAppStateLoading,
    ldClient,
    updateSiteInContext,
  };

  /*
    Children must be allowed through here to allow the authentication
    components to run and sign in the user due to the way the legacy code works.
    That's why we need to check the isAppStateLoading flag in router.tsx as well
    as call setSignedIn from authenticator.tsx. Once we can stop supporting
    the legacy redux code, we should be able to encapsulate the authentication
    in this component making it much cleaner without needing to expose any of
    this logic to the rest of the app.
  */
  return (
    <AppContext.Provider value={appState} {...props}>
      <>
        {isAppStateLoading && <AppLoader />}
        {props.children}
      </>
    </AppContext.Provider>
  );
}

/**
 * This hook provides access to utility functions that allow you to check the
 * status of a feature flag. Example usage:
 *
 * const {flagEnabled} = useFlags();
 * const featureXEnabled = flagEnabled('feature-x');
 *
 * flagEnabled() works only with boolean flags, as does anyFlagEnabled() and
 * allFlagsEnabled().
 *
 * For multivariant flags, use getFlagVariation().
 *
 * For more info: https://github.com/SaleCycle/my-salecycle-com/wiki/Feature-Flags
 */
export function useFeatureFlags() {
  const context: AppState = React.useContext(AppContext);
  if (!context) {
    throw new Error(`useFlags must be used within a AppProvider`);
  }

  return {
    flagEnabled: _flagEnabled(context),
    anyFlagEnabled: _anyFlagEnabled(context),
    allFlagsEnabled: _allFlagsEnabled(context),
    getFlagVariation: _getFlagVariation(context),
  };
}

export function usePermissions() {
  const context: AppState = React.useContext(AppContext);
  if (!context) {
    throw new Error(`usePermissions must be used within a AppProvider`);
  }

  return {
    hasPermission: _hasPermission(context),
    hasPermissionsAll: _hasPermissionsAll(context),
    hasPermissionsAtLeastOne: _hasPermissionsAtLeastOne(context),
  };
}

/**
 * @deprecated Consider using useSite instead. This is a transitional hook
 * useful to help us modernise the legacy code step-by-step. It's fine to use
 * for now but it will be deprecated eventually and ideally, all new code should
 * fetch only the client data needed. Once all code works this way, we will no
 * longer need to keep a "local database" of client data in AppContext and then
 * we can remove useClients as well
 */
export function useClients() {
  const context: AppState = React.useContext(AppContext);
  if (!context) {
    throw new Error(`useClients must be used within a AppProvider`);
  }

  /*
    In reality there is no need to optionally chain context below because by the
    time useClients is invoked, we can be safe in the knowledge all clients have
    been loaded. However, it is theoretically possible we might have no clients
    at all, which is also true in some of our unit tests so that's why we have
    optional chaining here. 
  */

  const getGroups = React.useCallback(() => context.clients.groups, [context?.clients?.groups]);
  const getBrands = React.useCallback(() => context.clients.brands, [context?.clients?.brands]);
  const getSites = React.useCallback(() => context.clients.sites, [context?.clients?.sites]);
  const getGroup = React.useCallback((groupId: string) => context.clients.groups.find((g: Group) => g.id === groupId), [
    context?.clients?.groups,
  ]);
  const getBrand = React.useCallback((brandId: string) => context.clients.brands.find((b: Brand) => b.id === brandId), [
    context?.clients?.brands,
  ]);
  const getSite = React.useCallback((siteId: string) => context.clients.sites.find((s: Site) => s.id === siteId), [
    context?.clients?.sites,
  ]);

  return {
    getGroups,
    getBrands,
    getSites,
    getGroup,
    getBrand,
    getSite,
  };
}

export function useAccessLevel() {
  const context: AppState = React.useContext(AppContext);
  if (!context) {
    throw new Error(`useAccessLevel must be used within a AppProvider`);
  }

  return {
    isSiteAuthorised: _isSiteAuthorised(context),
    isBrandAuthorised: _isBrandAuthorised(context),
    accessLevel: context?.userInfo?.accessLevel,
    groupIds: context?.userInfo?.groupIds,
    brandIds: context?.userInfo?.brandIds,
    siteIds: context?.userInfo?.siteIds,
  };
}

export function useAppContext() {
  const context: AppState = React.useContext(AppContext);
  if (!context) {
    throw new Error(`useAppContext must be used within a AppProvider`);
  }

  const isSignedIn = () => context.signedIn;
  return {
    isAppStateLoading: context.isAppStateLoading,
    isSignedIn,
    setSignedIn: context.setSignedIn,
  };
}

export function useUserDetails() {
  const context: AppState = React.useContext(AppContext);
  if (!context) {
    throw new Error(`useUserDetails must be used within a AppProvider`);
  }
  return {
    username: context.userDetails.username,
    email: context.userDetails.email,
  };
}

async function fetchClients(store) {
  const res = await RestApi.get<Clients>(`/clients`);

  /*
    We're temporarily continuing to store clients in Redux for now but this is
    only needed for backwards compatibility with legacy code where client data
    is still accessed from Redux.
  */
  const mapFromResponse = (response: any) => ({ id: response.id, entity: response });
  store.dispatch({
    type: ApiGatewayTypes.GET_COLLECTION_COMPLETED,
    service: Service.Groups,
    payload: { entities: res.data.groups.map((g: Group) => mapFromResponse(g)) },
  });
  store.dispatch({
    type: ApiGatewayTypes.GET_COLLECTION_COMPLETED,
    service: Service.Brands,
    payload: { entities: res.data.brands.map((b: Brand) => mapFromResponse(b)) },
  });
  store.dispatch({
    type: ApiGatewayTypes.GET_COLLECTION_COMPLETED,
    service: Service.Sites,
    payload: { entities: res.data.sites.map((s: Site) => mapFromResponse(s)) },
  });
  return res.data;
}

/**
 *
 * @deprecated Prefer useFeatureFlags instead.
 * For more info: https://github.com/SaleCycle/my-salecycle-com/wiki/Feature-Flags
 */
export function useFlags() {
  const context: AppState = React.useContext(AppContext);
  if (!context) {
    throw new Error(`useFlags must be used within a AppProvider`);
  }

  return {
    hasFlag: _hasFlag(context),
    hasFlags: _hasFlags(context),
  };
}

export function useUpdateSiteInContext() {
  const context = React.useContext(AppContext);
  if (!context || !context.updateSiteInContext) {
    throw new Error('useUpdateSiteInContext must be used within a AppProvider');
  }
  return context.updateSiteInContext;
}
